if (!customElements.get('card-slider')) {
    class CardSlider extends HTMLElement {
        constructor() {
            super()

            this.init()
        }

        init() {
            const carousel = new Carousel(this, {
                infinite: false,
                center: false,
                slidesPerPage: 1,
                transition: false,
                Navigation: {
                    prevTpl: '<svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.5 11L1.5 6L6.5 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
                    nextTpl: '<svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
                },
            })
        }
    }

    customElements.define('card-slider', CardSlider);
}

if (!customElements.get('price-range')) {
    class PriceRange extends HTMLElement {
        constructor() {
            super();

            this.minRange = this.querySelector("#range-min");
            this.maxRange = this.querySelector("#range-max");
            this.minPrice = this.querySelector("#min-price");
            this.maxPrice = this.querySelector("#max-price");
            this.minPriceDisplay = this.querySelector("#min-price-display");
            this.maxPriceDisplay = this.querySelector("#max-price-display");
            this.rangeBar = this.querySelector(".range-bar");
            this.rangeGap = 5;
            this.currencySymbol = this.dataset.currencySymbol || 'R$';
            this.updateTimeout = null;

            this.listener();
        }

        listener() {
            this.minRange?.addEventListener("input", this.changeMinRange.bind(this));
            this.maxRange?.addEventListener("input", this.changeMaxRange.bind(this));
            this.minRange?.addEventListener("change", this.applyFilter.bind(this));
            this.maxRange?.addEventListener("change", this.applyFilter.bind(this));

            const event = new Event('input');
            this.minRange?.dispatchEvent(event);
            this.maxRange?.dispatchEvent(event);
        }

        formatCurrency(value) {
            return new Intl.NumberFormat('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        }

        changeMinRange(event) {
            const target = event.currentTarget;

            if (parseFloat(this.maxRange.value) - parseFloat(this.minRange.value) <= this.rangeGap) {
                this.minRange.value = parseFloat(this.maxRange.value) - this.rangeGap;
            }

            const minValue = parseFloat(this.minRange.value);
            this.minPrice.value = minValue.toFixed(2);
            this.minPriceDisplay.textContent = `${this.currencySymbol} ${this.formatCurrency(minValue)}`;

            let percent = ((this.minRange.value - this.minRange.min) / (this.maxRange.max - this.minRange.min)) * 100;
            this.rangeBar.style.left = percent + "%";
        }

        changeMaxRange(event) {
            const target = event.currentTarget;

            if (parseFloat(this.maxRange.value) - parseFloat(this.minRange.value) <= this.rangeGap) {
                this.maxRange.value = parseFloat(this.minRange.value) + this.rangeGap;
            }

            const maxValue = parseFloat(this.maxRange.value);
            this.maxPrice.value = maxValue.toFixed(2);
            this.maxPriceDisplay.textContent = `${this.currencySymbol} ${this.formatCurrency(maxValue)}`;

            let percent = ((this.maxRange.value - this.minRange.min) / (this.maxRange.max - this.minRange.min)) * 100;
            this.rangeBar.style.right = 100 - percent + "%";
        }

        applyFilter() {
            // Limpa o timeout anterior se existir
            if (this.updateTimeout) {
                clearTimeout(this.updateTimeout);
            }

            // Define um pequeno delay para evitar múltiplas submissões
            this.updateTimeout = setTimeout(() => {
                const form = this.closest('form');
                if (form) {
                    // Dispara um evento customizado para que o script de filtros processe
                    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                }
            }, 300);
        }
    }

    customElements.define("price-range", PriceRange);
}

if (!customElements.get('list-range')) {
    class ListRange extends HTMLElement {
        constructor() {
            super();

            this.minRange = this.querySelector("#range-min");
            this.maxRange = this.querySelector("#range-max");
            this.minDisplay = this.querySelector("#min-price-display");
            this.maxDisplay = this.querySelector("#max-price-display");
            this.rangeBar = this.querySelector(".range-bar");
            this.updateTimeout = null;
            this.labelSuffix = this.dataset.labelSuffix || '';

            try {
                this.values = JSON.parse(this.dataset.values || "[]");
            } catch (e) {
                this.values = [];
            }

            this.inputs = Array.from(this.querySelectorAll("[data-list-range-value]"));

            this.init();
        }

        init() {
            if (!this.minRange || !this.maxRange || this.values.length === 0) return;

            const activeIndices = this.values
                .map((value, index) => (value.active ? index : null))
                .filter((index) => index !== null);

            const minIndex = activeIndices.length ? Math.min(...activeIndices) : 0;
            const maxIndex = activeIndices.length ? Math.max(...activeIndices) : this.values.length - 1;

            this.minRange.value = minIndex;
            this.maxRange.value = maxIndex;

            this.minRange.addEventListener("input", this.changeMinRange.bind(this));
            this.maxRange.addEventListener("input", this.changeMaxRange.bind(this));
            this.minRange.addEventListener("change", this.applyFilter.bind(this));
            this.maxRange.addEventListener("change", this.applyFilter.bind(this));

            this.updateDisplays();
        }

        changeMinRange() {
            if (parseInt(this.minRange.value, 10) > parseInt(this.maxRange.value, 10)) {
                this.minRange.value = this.maxRange.value;
            }

            this.updateDisplays();
        }

        changeMaxRange() {
            if (parseInt(this.maxRange.value, 10) < parseInt(this.minRange.value, 10)) {
                this.maxRange.value = this.minRange.value;
            }

            this.updateDisplays();
        }

        updateDisplays() {
            const minIndex = parseInt(this.minRange.value, 10);
            const maxIndex = parseInt(this.maxRange.value, 10);

            if (this.minDisplay) {
                const minLabel = this.values[minIndex]?.label || '';
                this.minDisplay.textContent = this.labelSuffix ? `${minLabel} ${this.labelSuffix}` : minLabel;
            }

            if (this.maxDisplay) {
                const maxLabel = this.values[maxIndex]?.label || '';
                this.maxDisplay.textContent = this.labelSuffix ? `${maxLabel} ${this.labelSuffix}` : maxLabel;
            }

            const minPercent = ((minIndex - this.minRange.min) / (this.maxRange.max - this.minRange.min)) * 100;
            const maxPercent = ((maxIndex - this.minRange.min) / (this.maxRange.max - this.minRange.min)) * 100;

            if (this.rangeBar) {
                this.rangeBar.style.left = minPercent + "%";
                this.rangeBar.style.right = 100 - maxPercent + "%";
            }
        }

        applyFilter() {
            if (this.updateTimeout) {
                clearTimeout(this.updateTimeout);
            }

            this.updateTimeout = setTimeout(() => {
                const minIndex = parseInt(this.minRange.value, 10);
                const maxIndex = parseInt(this.maxRange.value, 10);

                this.inputs.forEach((input) => {
                    const index = parseInt(input.dataset.listRangeIndex, 10);
                    input.checked = index >= minIndex && index <= maxIndex;
                });

                const form = this.closest('form');
                if (form) {
                    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                }
            }, 300);
        }
    }

    customElements.define("list-range", ListRange);
}

if (!customElements.get('categories-scrollbar')) {
    class CategoriesScrollbar extends HTMLElement {
        constructor() {
            super();

            this.handleScroll = this.handleScroll.bind(this);
            this.handleResize = this.handleResize.bind(this);
        }

        connectedCallback() {
            this.carousel = this.querySelector(".categories-showcase__carousel");
            this.scrollbar = this.querySelector(".categories-showcase__scrollbar");

            if (!this.carousel || !this.scrollbar) {
                return;
            }

            this.thumb = this.scrollbar.querySelector(".categories-showcase__scrollbar-thumb");

            if (!this.thumb) {
                return;
            }

            this.updateThumb();

            this.carousel.addEventListener("scroll", this.handleScroll);
            window.addEventListener("resize", this.handleResize);
        }

        disconnectedCallback() {
            if (this.carousel) {
                this.carousel.removeEventListener("scroll", this.handleScroll);
            }

            window.removeEventListener("resize", this.handleResize);
        }

        handleScroll() {
            this.updateThumb();
        }

        handleResize() {
            this.updateThumb();
        }

        updateThumb() {
            var carousel = this.carousel;
            var scrollbar = this.scrollbar;
            var thumb = this.thumb;

            if (!carousel || !scrollbar || !thumb) {
                return;
            }

            var scrollWidth = carousel.scrollWidth;
            var clientWidth = carousel.clientWidth;
            var scrollLeft = carousel.scrollLeft;

            if (scrollWidth <= clientWidth) {
                scrollbar.style.visibility = "hidden";
                return;
            }

            scrollbar.style.visibility = "visible";

            var visibleRatio = clientWidth / scrollWidth;
            var thumbWidth = visibleRatio * 100;

            if (thumbWidth < 10) {
                thumbWidth = 10;
            }

            if (thumbWidth > 25) {
                thumbWidth = 25;
            }

            var maxTranslate = 100 - thumbWidth;
            var scrollRatio = scrollLeft / (scrollWidth - clientWidth);
            var translatePercent = maxTranslate * scrollRatio;

            thumb.style.width = thumbWidth + "%";
            thumb.style.left = translatePercent + "%";
        }
    }

    customElements.define("categories-scrollbar", CategoriesScrollbar);
}
